// The Company place (M11): getting started (onboarding.status and the starter setup), and for the
// owner the subscription (our billing, through the provider's hosted checkout) and downloading all
// of the company's data. Each step is the same action the rest of the app uses.

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { refusalOf, RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { Loading } from '../shared/Loading.tsx';
import {
  BC_WEEK_LINE,
  hoursAsMinutes,
  invitedWords,
  isBcRules,
  minutesAsHours,
  OVERTIME_PARTS,
  settingsRights,
  weekWarning,
  type Overtime,
} from '../settings/settings-model.ts';
import { dayText, todayIn } from '../books/model.ts';
import {
  firstDayOf,
  firstPeriod,
  PAY_EVERY,
  periodNote,
  saveWords,
  weekChoices,
  weekdayOf,
  type PayEvery,
} from './payroll-step-model.ts';
import { RuleSections } from '../settings/RuleSection.tsx';
import { FindSetting } from '../settings/FindSetting.tsx';
import { OrgTab, PaperworkSection } from '../settings/Setup.tsx';
import { setupRights } from '../settings/setup-model.ts';
import { Header, type NavProps } from '../Nav.tsx';
import { useDesign } from '../blueprint/design.ts';
import { Label, Meter, Section } from '@flowology/ui-web';
import {
  money,
  nextStep,
  provinceFromTimeZone,
  subscriptionLine,
  type Plan,
  type Step,
  type Subscription,
} from './account-model.ts';
import { useStepUp } from '../shared/StepUpAsk.tsx';
import './account.css';
import '@flowology/ui-web/base.css';
import '../blueprint/setup.css';

type Part = 'start' | 'subscription' | 'data';
// The one line under the title, for each part.
const LEDE: Record<Part, string> = {
  start: 'The basics a new company sets up once; each step takes a minute.',
  subscription: 'Your plan with Flowology, and paying for it.',
  data: 'Everything your company has in Flowology, yours to download any time.',
};

const BP_DETAILS_LEDE = 'Who you are on paper, your departments, and how you look to customers.';
const BP_PLAN_LEDE = 'Your plan with Flowology, and everything your company has here, yours to download.';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

/** The billing routes are plain HTTP (not actions), so a read-only company can always pay. */
export async function billing<T>(method: 'GET' | 'POST', path: string): Promise<T> {
  const csrf = method === 'POST' ? (await client.session())?.csrf : undefined;
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      ...(method === 'POST' ? { 'content-type': 'application/json' } : {}),
      ...(csrf ? { 'x-flow-csrf': csrf } : {}),
    },
    ...(method === 'POST' ? { body: '{}' } : {}),
  });
  const body = (await res.json().catch(() => null)) as {
    ok?: boolean;
    result?: T;
    error?: unknown;
  } | null;
  // A refusal says what to do, ours or the way there's (a 413, a proxy's 502), as the client does.
  if (!res.ok || !body?.ok) throw new RpcError(res.status, refusalOf(res.status, body));
  return body.result as T;
}

const PROVINCES: [string, string][] = [
  ['AB', 'Alberta'],
  ['BC', 'British Columbia'],
  ['MB', 'Manitoba'],
  ['NB', 'New Brunswick'],
  ['NL', 'Newfoundland and Labrador'],
  ['NS', 'Nova Scotia'],
  ['NT', 'Northwest Territories'],
  ['NU', 'Nunavut'],
  ['ON', 'Ontario'],
  ['PE', 'Prince Edward Island'],
  ['QC', 'Québec'],
  ['SK', 'Saskatchewan'],
  ['YT', 'Yukon'],
];

export function CompanyScreen({
  nav,
  plan = false,
}: {
  nav: NavProps;
  /** Blueprint's Setup › Your plan and data: the subscription and the data, a page of their own. */
  plan?: boolean;
}) {
  const bp = useDesign() === 'blueprint';
  // by the permissions the person holds, whichever role gives them (QA-681): the subscription is
  // billing.manage's, the data's download data.export's (both stay with the Owner role)
  const held = nav.permissions ?? [];
  const owner = held.includes('billing.manage');
  const ownsData = held.includes('data.export');
  const canSetUp = held.includes('settings.edit');
  const mirroring = useMirror();
  const parts: { part: Part; label: string }[] = [
    { part: 'start', label: 'Getting started' },
    ...(owner ? [{ part: 'subscription' as const, label: 'Subscription' }] : []),
    ...(ownsData ? [{ part: 'data' as const, label: 'Your data' }] : []),
  ];
  // The provider's checkout and the banner's "Pay now" come back to #billing.
  const [part, setPart] = useState<Part>(owner && location.hash === '#billing' ? 'subscription' : 'start');
  useEffect(() => {
    if (location.hash === '#billing') history.replaceState(null, '', location.pathname + location.search);
  }, []);
  // Blueprint (the setup audit): Company details holds Getting started, the paperwork, the departments
  // and how you look; the plan and data are their own page, which #billing comes back to as well
  if (bp && (plan || part !== 'start'))
    return (
      <div className="app company bp-setup flw">
        <Header nav={nav} title="Your plan and data" lede={BP_PLAN_LEDE} />
        <main className="company-main">
          {owner && <SubscriptionPart />}
          {ownsData && <DataPart />}
        </main>
      </div>
    );
  if (bp) {
    const canEdit = setupRights(held).org;
    return (
      <div className="app company bp-setup flw">
        <Header nav={nav} title="Company details" lede={BP_DETAILS_LEDE} />
        <main className="company-main">
          {/* Setup's first page: any rule, by a word, on the pages this person opens */}
          <FindSetting held={held} />
          <GettingStarted
            canSetUp={canSetUp}
            owner={owner}
            timeZone={nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}
            onBook={() => nav.onView('book')}
            onSignIn={() => nav.onView('signin')}
          />
          <PaperworkSection canEdit={canEdit} held={held} />
          {mirroring && canEdit && (
            <p className="muted small">
              Departments’ names: {MIRROR_LINE} A department’s customer text stays yours.
            </p>
          )}
          <OrgTab canEdit={canEdit} mirroring={mirroring} show="departments" />
          <RuleSections page="company" rights={settingsRights(held)} />
        </main>
      </div>
    );
  }
  return (
    <div className="app company">
      <Header nav={nav} title="Company" lede={LEDE[part]} />
      <div className="company-body">
        {parts.length > 1 && (
          <nav className="company-parts" aria-label="Company">
            {parts.map((p) => (
              <button
                key={p.part}
                className={`btn quiet${part === p.part ? ' on' : ''}`}
                aria-current={part === p.part ? 'page' : undefined}
                onClick={() => setPart(p.part)}
              >
                {p.label}
              </button>
            ))}
          </nav>
        )}
        <main className="company-main">
          {part === 'start' && (
            <GettingStarted
              canSetUp={canSetUp}
              owner={owner}
              timeZone={nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}
              onBook={() => nav.onView('book')}
              onSignIn={() => nav.onView('signin')}
            />
          )}
          {part === 'subscription' && owner && <SubscriptionPart />}
          {part === 'data' && ownsData && <DataPart />}
        </main>
      </div>
    </div>
  );
}

function GettingStarted({
  canSetUp,
  owner,
  timeZone,
  onBook,
  onSignIn,
}: {
  canSetUp: boolean;
  owner: boolean;
  timeZone: string;
  onBook: () => void;
  onSignIn: () => void;
}) {
  const [steps, setSteps] = useState<Step[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [skipped, setSkipped] = useState<ReadonlySet<string>>(new Set());
  // The trial's end, for the owner (QA-336).
  const [trial, setTrial] = useState<string | null>(null);
  const load = useCallback(() => {
    void Promise.all([
      client.call<{ steps: Step[] }>('onboarding.status', {}),
      // Your own two-factor (QA-329): a step for the person, not the company's data.
      client.me<{ on: boolean; required: boolean }>('GET', '/me/2fa').catch(() => null),
    ])
      .then(([r, tfa]) =>
        setSteps([
          ...r.steps,
          ...(tfa
            ? [{ key: 'twoFactor', label: 'Two-factor sign-in for you', done: tfa.on, required: false }]
            : []),
        ]),
      )
      .catch((e: unknown) => setError(message(e)));
    if (owner)
      void billing<{ subscription: Subscription | null }>('GET', '/billing')
        .then((b) =>
          setTrial(b.subscription?.status === 'trialing' ? subscriptionLine(b.subscription, timeZone) : null),
        )
        .catch(() => setTrial(null));
  }, [owner, timeZone]);
  useEffect(load, [load]);
  // Blueprint: a list shows five rows, then all of them (DESIGN.md §3)
  const bp = useDesign() === 'blueprint';
  const [all, setAll] = useState(false);
  if (!steps) return <Loading error={error} what="your getting-started steps" retry={load} />;
  const next = nextStep(steps, skipped);
  const done = steps.filter((s) => s.done).length;
  // what the next step asks, the same in either design
  const nextBody = next && (
    <>
      {next.key === 'firstJob' ? (
        <>
          <p>Book your first job: a customer, a time and a technician.</p>
          <button className="btn primary" onClick={onBook}>
            Book a job
          </button>
        </>
      ) : next.key === 'twoFactor' ? (
        <>
          <p>A second step when you sign in, so a stolen password isn’t enough.</p>
          <button className="btn primary" onClick={onSignIn}>
            Set up two-factor
          </button>
        </>
      ) : next.key === 'texting' ? (
        <p>
          Confirmations, “on the way” texts and pay links go from your company’s own number. Flowology
          connects it for you: ask us, and we’ll set up your number.
        </p>
      ) : next.key === 'payments' ? (
        <p>
          Taking cards on site needs your card processor and a terminal. Flowology connects them for you: ask
          us when you’re ready.
        </p>
      ) : next.key === 'payroll' ? (
        // the pay schedule and payroll's rules are the owner's (payroll.send, payroll.prepare)
        owner ? (
          <PayrollStep timeZone={timeZone} onDone={load} />
        ) : (
          <p>Ask the owner to finish this step. Hours and timesheets work in the meantime.</p>
        )
      ) : canSetUp ? (
        <StepForm step={next} timeZone={timeZone} onDone={load} />
      ) : (
        <p>Ask the owner or a manager to finish this step.</p>
      )}
      {!next.required && (
        <button className="btn quiet" onClick={() => setSkipped(new Set([...skipped, next.key]))}>
          Skip for now
        </button>
      )}
    </>
  );
  if (bp) {
    // what's left first, in the list's order; the done ones once the list is opened in full
    const left = steps.filter((s) => !s.done);
    const shown = all ? steps : (left.length ? left : steps).slice(0, 5);
    // one section, what's next a part of it beside the steps: Company details keeps its paperwork,
    // departments and look within the first screen (three zones above the fold, DESIGN.md §3a)
    return (
      <Section title={next ? 'Getting started' : 'You’re set up'}>
        <div className="bp-grid2 company-start">
          <div>
            <p className="muted">
              {done} of {steps.length} done.
              {next ? ' Each step takes a minute; you can change everything later in settings.' : ''}
            </p>
            <Meter value={done} max={steps.length} label={`${done} of ${steps.length} steps done`} />
            {trial && <p className="notice">{trial}</p>}
            <ol className="bp-rows company-steps">
              {shown.map((s) => (
                <li key={s.key} className={s.done ? 'done' : s === next ? 'next' : ''}>
                  <span>
                    {s.label}
                    {!s.required && <span className="muted"> (optional)</span>}
                  </span>
                  {s.done ? (
                    <Label>Done</Label>
                  ) : s === next ? (
                    <Label kind="now">Now</Label>
                  ) : (
                    <span className="sr-only">not done yet</span>
                  )}
                </li>
              ))}
            </ol>
            {shown.length < steps.length && (
              <button className="bp-more" onClick={() => setAll(true)}>
                Show all {steps.length}
              </button>
            )}
          </div>
          {next && nextBody && (
            <div className="bp-part" role="group" aria-label={`Now: ${next.label}`}>
              <h3>Now: {next.label}</h3>
              <div className="company-next">{nextBody}</div>
            </div>
          )}
        </div>
      </Section>
    );
  }
  return (
    <section className="sheet start" aria-labelledby="start-h">
      <h2 id="start-h">{next ? 'Getting started' : 'You’re set up'}</h2>
      <p className="muted">
        {done} of {steps.length} done.
        {next ? ' Each step takes a minute; you can change everything later in settings.' : ''}
      </p>
      {trial && <p className="trial">{trial}</p>}
      <ol className="steps">
        {steps.map((s) => (
          <li key={s.key} className={s.done ? 'done' : s === next ? 'next' : ''}>
            <span className="tick" aria-hidden="true">
              {s.done ? '✓' : ''}
            </span>
            <span>
              {s.label}
              {!s.required && <span className="muted"> (optional)</span>}
              {s === next && <span className="nextword"> Now</span>}
              <span className="sr-only">{s.done ? ' — done' : ' — not done yet'}</span>
            </span>
          </li>
        ))}
      </ol>
      {next && <div className="stone step-form">{nextBody}</div>}
    </section>
  );
}

function StepForm({ step, timeZone, onDone }: { step: Step; timeZone: string; onDone: () => void }) {
  // a price book item and the starter (departments, job types, tax) are ServiceTitan's while the
  // company mirrors it (BL-088)
  const mirroring = useMirror();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [province, setProvince] = useState(() => provinceFromTimeZone(timeZone));
  const [role, setRole] = useState('csr');
  // What an invitation sent (or what to tell them): kept over the next step (QA-322).
  const [note, setNote] = useState<string | null>(null);
  // an invitation that hands out access asks a fresh second factor (QA-685): these never do, but the
  // code box is there if the server asks
  const { run, ask } = useStepUp();
  useEffect(() => {
    setA('');
    setB('');
    setError(null);
  }, [step.key]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const call =
      step.key === 'profile'
        ? client.call('settings.set', { key: 'company.phone', value: a })
        : step.key === 'pricebook'
          ? client.call('pricebook.createItem', {
              type: 'service',
              code: a
                .toUpperCase()
                .replace(/[^A-Z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '')
                .slice(0, 20),
              name: a,
              priceCents: Math.round(Number(b) * 100),
            })
          : step.key === 'invites'
            ? run(() =>
                client.call<{ emailed: boolean; signInAt: string | null }>('people.invite', {
                  email: b.trim(),
                  displayName: a,
                  roleKeys: [role],
                }),
              ).then((r) => setNote(invitedWords(r, a.trim(), b.trim())))
            : step.key === 'team'
              ? b.trim()
                ? // With their email they're invited to sign in, as a technician (people.invite).
                  run(() =>
                    client.call<{ emailed: boolean; signInAt: string | null }>('people.invite', {
                      email: b.trim(),
                      displayName: a,
                      roleKeys: ['technician'],
                    }),
                  ).then((r) => setNote(invitedWords(r, a.trim(), b.trim())))
                : client.call('staff.create', { displayName: a, isTechnician: true })
              : client.call('onboarding.applyStarter', { province });
    void call
      .then(onDone)
      .catch((err: unknown) => setError(message(err)))
      .finally(() => setBusy(false));
  };
  if (mirroring && (step.key === 'pricebook' || step.key === 'tax'))
    return <p className="muted small">{MIRROR_LINE}</p>;
  return (
    <>
      <form onSubmit={submit} className="step-fields">
        {step.key === 'profile' && (
          <label>
            The phone number your customers call
            <input
              className="field"
              type="tel"
              autoComplete="tel"
              required
              value={a}
              onChange={(e) => setA(e.target.value)}
            />
          </label>
        )}
        {(step.key === 'businessUnit' || step.key === 'jobTypes' || step.key === 'tax') && (
          <>
            <p>
              We set up a department, three kinds of job (service call, estimate, maintenance) and your
              province’s sales tax. Only what you don’t have yet.
            </p>
            <label>
              Your province or territory
              <select className="field" value={province} onChange={(e) => setProvince(e.target.value)}>
                {PROVINCES.map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
        {step.key === 'pricebook' && (
          <>
            <label>
              A service you sell
              <input className="field" required value={a} onChange={(e) => setA(e.target.value)} />
            </label>
            <label>
              Its price, in dollars
              <input
                className="field"
                inputMode="decimal"
                required
                pattern="[0-9]+(\.[0-9]{1,2})?"
                value={b}
                onChange={(e) => setB(e.target.value)}
              />
            </label>
          </>
        )}
        {note && <p role="status">{note}</p>}
        {step.key === 'team' && (
          <>
            <label>
              A technician’s name
              <input className="field" required value={a} onChange={(e) => setA(e.target.value)} />
            </label>
            <label>
              Their email, to invite them to sign in (optional)
              <span className="muted small hint">
                They sign in on the iPad with it. You can invite them later in Settings, People.
              </span>
              <input
                className="field"
                type="email"
                autoComplete="off"
                value={b}
                onChange={(e) => setB(e.target.value)}
              />
            </label>
          </>
        )}
        {step.key === 'invites' && (
          <>
            <label>
              Their name
              <input className="field" required value={a} onChange={(e) => setA(e.target.value)} />
            </label>
            <label>
              Their email, for the invitation
              <input
                className="field"
                type="email"
                autoComplete="off"
                required
                value={b}
                onChange={(e) => setB(e.target.value)}
              />
            </label>
            <label>
              What they do
              <select className="field" value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="csr">Answers the phone and books jobs</option>
                <option value="dispatcher">Dispatches the techs</option>
                <option value="bookkeeper">Keeps the books</option>
                <option value="manager">Runs the office</option>
              </select>
            </label>
          </>
        )}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button className="btn primary" disabled={busy}>
          {step.key === 'profile'
            ? 'Save the phone number'
            : step.key === 'pricebook'
              ? 'Add the price'
              : step.key === 'invites'
                ? 'Send the invitation'
                : step.key === 'team'
                  ? b.trim()
                    ? 'Add and invite the technician'
                    : 'Add the technician'
                  : 'Set up the basics'}
        </button>
      </form>
      {ask}
    </>
  );
}

/**
 * Getting started's "How you'll pay your team" (BL-172): how often and the first period (as Payroll's
 * own first period, payPeriods.create), the overtime week (Sunday first under BC's rules, a plain
 * warning for another day, never a refusal), the overtime rules (BC's by default) and who runs payroll,
 * "None yet" included. Saving the provider is what marks the step done when no period opens (a company
 * mirroring its old system opens its periods after the switch). Time tracking never waits on it.
 */
function PayrollStep({ timeZone, onDone }: { timeZone: string; onDone: () => void }) {
  const mirroring = useMirror();
  const [now, setNow] = useState<{ week: number; overtime: Overtime } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [every, setEvery] = useState<PayEvery>(14);
  // the first day follows the week until it's typed
  const [firstDay, setFirstDay] = useState<string | null>(null);
  const [week, setWeek] = useState<number | null>(null);
  const [hours, setHours] = useState<Record<string, string> | null>(null);
  const [named, setNamed] = useState(false);
  const [provider, setProvider] = useState('');
  // a period opened before a later save failed: not opened twice
  const [opened, setOpened] = useState(false);
  const load = useCallback(() => {
    setError(null);
    void Promise.all([
      client.call<{ value: unknown }>('settings.get', { key: 'payroll.weekStartsOn' }),
      client.call<{ value: unknown }>('settings.get', { key: 'payroll.overtime' }),
    ])
      .then(([w, o]) =>
        setNow({ week: typeof w.value === 'number' ? w.value : 0, overtime: o.value as Overtime }),
      )
      .catch((e: unknown) => setError(message(e)));
  }, []);
  useEffect(load, [load]);
  if (!now) return <Loading error={error} what="payroll’s rules" retry={load} />;
  const typed =
    hours ?? Object.fromEntries(OVERTIME_PARTS.map(([k]) => [k, minutesAsHours(now.overtime[k])]));
  const parsed = Object.fromEntries(OVERTIME_PARTS.map(([k]) => [k, hoursAsMinutes(typed[k] ?? '')]));
  const overtime = Object.values(parsed).some((m) => m === null) ? null : (parsed as unknown as Overtime);
  const bc = isBcRules(overtime ?? now.overtime);
  const w = week ?? now.week;
  const start = firstDay ?? firstDayOf(todayIn(timeZone), w, every);
  const period = mirroring || opened ? null : firstPeriod(start, every);
  const warning = weekWarning(w, bc);
  const note = period ? periodNote(start, w) : null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!overtime) return setError('Enter each as hours, like 8 or 7.5.');
    if (named && !provider.trim()) return setError('Name your payroll provider, or choose None yet.');
    setBusy(true);
    setError(null);
    try {
      if (w !== now.week) await client.call('settings.set', { key: 'payroll.weekStartsOn', value: w });
      if (JSON.stringify(overtime) !== JSON.stringify(now.overtime))
        await client.call('settings.set', { key: 'payroll.overtime', value: overtime });
      if (period) {
        await client.call('payPeriods.create', period);
        setOpened(true);
      }
      await client.call('settings.set', { key: 'payroll.provider', value: named ? provider.trim() : '' });
      onDone();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="step-fields pay-step"
      aria-label="How you’ll pay your team"
    >
      <p>
        How often you pay, the week overtime is counted in, and who runs payroll. Hours and timesheets work
        without it; this starts pay periods and locking. You can change it all later in Setup, Payroll.
      </p>
      {mirroring ? (
        <p className="muted small">Your first pay period in Flowology opens after the switch.</p>
      ) : opened ? null : (
        <>
          <fieldset>
            <legend>How often you pay</legend>
            {PAY_EVERY.map(([n, label]) => (
              <label key={n} className="pick">
                <input type="radio" name="pay-every" checked={every === n} onChange={() => setEvery(n)} />
                {label}
              </label>
            ))}
          </fieldset>
          <label>
            The first pay period starts
            <input
              className="field"
              type="date"
              required
              value={start}
              onChange={(e) => setFirstDay(e.target.value || null)}
            />
            {period && (
              <span className="muted small hint">
                {dayText(period.startDate)} to {dayText(period.endDate)}, paid {dayText(period.payDate)}.
              </span>
            )}
          </label>
        </>
      )}
      <label>
        The overtime week starts on
        <select className="field" value={w} onChange={(e) => setWeek(Number(e.target.value))}>
          {weekChoices(bc, period ? weekdayOf(start) : null).map(([d, name]) => (
            <option key={d} value={d}>
              {name}
            </option>
          ))}
        </select>
        {warning ? (
          <span className="warn-text small" role="status">
            {warning}
          </span>
        ) : bc ? (
          <span className="muted small hint">{BC_WEEK_LINE}</span>
        ) : null}
        {note && <span className="muted small hint">{note}</span>}
      </label>
      <fieldset>
        <legend>Overtime starts after</legend>
        <span className="muted small hint">
          BC’s: 8 hours a day, double time after 12, and 40 hours a week.
        </span>
        {OVERTIME_PARTS.map(([k, label]) => (
          <label key={k} className="pick">
            <input
              className="field hours"
              size={5}
              inputMode="decimal"
              aria-label={label}
              value={typed[k] ?? ''}
              onChange={(e) => setHours({ ...typed, [k]: e.target.value })}
            />
            {label}
          </label>
        ))}
      </fieldset>
      <fieldset>
        <legend>Who runs your payroll</legend>
        <label className="pick">
          <input type="radio" name="pay-provider" checked={!named} onChange={() => setNamed(false)} />
          None yet
        </label>
        <label className="pick">
          <input type="radio" name="pay-provider" checked={named} onChange={() => setNamed(true)} />A payroll
          provider
        </label>
        {named && (
          <label>
            The provider’s name
            <input
              className="field"
              required
              maxLength={100}
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
            />
          </label>
        )}
      </fieldset>
      <p className="muted small">{saveWords(period, named ? provider : '')}</p>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <button className="btn primary" disabled={busy}>
        {busy ? 'Saving…' : 'Save how you pay'}
      </button>
    </form>
  );
}

function SubscriptionPart() {
  const [data, setData] = useState<{ subscription: Subscription | null; plan: Plan | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const load = useCallback(() => {
    void billing<{ subscription: Subscription | null; plan: Plan | null }>('GET', '/billing')
      .then(setData)
      .catch((e: unknown) => setError(message(e)));
  }, []);
  useEffect(load, [load]);
  if (!data) return <Loading error={error} what="your subscription" retry={load} />;
  const { subscription: sub, plan } = data;
  if (!sub)
    return (
      <section className="sheet">
        <h2>Subscription</h2>
        <p>Your company has no subscription with us to manage here.</p>
        <p className="muted small">
          Nothing to do: your plan is arranged with Flowology directly. To change it, the owner asks us
          through Help.
        </p>
      </section>
    );
  const pay = () => {
    setBusy(true);
    setError(null);
    void billing<{ url: string }>('POST', '/billing/checkout')
      .then((r) => location.assign(r.url))
      .catch((e: unknown) => {
        setError(message(e));
        setBusy(false);
      });
  };
  const cancel = () => {
    if (
      !confirm('Cancel the subscription? Your company becomes read-only after the paid time and the grace.')
    )
      return;
    setBusy(true);
    void billing('POST', '/billing/cancel')
      .then(load)
      .catch((e: unknown) => setError(message(e)))
      .finally(() => setBusy(false));
  };
  const paying = !!sub.providerSubscriptionId && sub.status !== 'canceled';
  return (
    <section className="sheet" aria-labelledby="sub-h">
      <h2 id="sub-h">Subscription</h2>
      <p className="lead">{subscriptionLine(sub, timeZone)}</p>
      {plan && (
        <dl className="plan">
          <dt>Plan</dt>
          <dd>{plan.name}</dd>
          <dt>Price</dt>
          <dd>
            {money(plan.priceCents, plan.currency)} a month, {plan.seatsIncluded} people included
            {plan.seatPriceCents > 0 && `, then ${money(plan.seatPriceCents, plan.currency)} a person`}
          </dd>
        </dl>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        {!paying && (
          <button className="btn primary" disabled={busy} onClick={pay}>
            {sub.status === 'trialing' ? 'Add a card' : 'Pay now'}
          </button>
        )}
        {paying && (
          <button className="btn" disabled={busy} onClick={cancel}>
            Cancel the subscription
          </button>
        )}
      </div>
      <p className="muted small">Cards are handled by our payment provider; we never see the card number.</p>
    </section>
  );
}

function DataPart() {
  return (
    <section className="sheet" aria-labelledby="data-h">
      <h2 id="data-h">Your data</h2>
      <p>
        Download everything your company has in Flowology: customers, jobs, estimates, invoices, payments, the
        books and the history. One spreadsheet file (CSV) per kind of record, in a ZIP. You can do this any
        time, also if your subscription ends.
      </p>
      <a className="btn primary" href="/export" download>
        Download all your data
      </a>
      <p className="muted small">
        Photos, signatures and PDFs are listed by name; ask us for a copy of the files themselves.
      </p>
    </section>
  );
}
